import * as React from 'react';
import { cn } from '@/shared/lib/cn';
import { useMaskedInput } from '@/shared/lib/useMaskedInput';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { Button } from '@/shared/ui/Button';
import { Calendar } from '@/shared/ui/Calendar/Calendar';
import {
  addDays,
  addMonths,
  completeRange,
  formatRuRange,
  isoToday,
  maskRangeEdit,
  orderRange,
  parseRuRange,
  startOfMonth,
  type DateRange,
} from '@/shared/ui/Calendar/calendar-date';
import { Input, type InputProps } from '@/shared/ui/Input/Input';
import { PickerShell } from './PickerShell';
import styles from './picker.module.css';

/**
 * Поле периода: `ДД.ММ.ГГГГ — ДД.ММ.ГГГГ` одной строкой плюс календарь на два
 * месяца.
 *
 * Одно поле, а не два рядом, потому что у контрола в форме подпись ровно одна
 * (UI_LANGUAGE, «Форма»), а «с» и «по» — это две подписи у одного значения.
 * Разделитель, как и точки, ставится маской: человек печатает шестнадцать цифр
 * подряд и ни разу не тянется к тире.
 *
 * Календарь показывает два месяца сразу — период почти всегда пересекает
 * границу месяца, и на одной сетке второй конец приходится выбирать вслепую,
 * пролистав первый. На узком экране месяц остаётся один: два не помещаются.
 *
 * Первый клик ставит начало, второй — конец; пока конец не выбран, отрезок
 * докрашивается до дня под курсором, иначе не видно, что календарь ждёт
 * второго клика. Концы, выбранные наоборот, меняются местами — «с 30-го по
 * 1-е» это ошибка ввода, а не другой смысл.
 *
 * @use ввод периода одним полем: две даты с маской и календарь на два месяца.
 */
export interface DateRangePickerProps
  extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'onBlur'> {
  value: DateRange;
  onChange: (value: DateRange) => void;
  min?: string;
  max?: string;
  isDisabled?: (iso: string) => boolean;
  presets?: boolean;
  clearable?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

function presetRanges(today: string): { label: string; range: DateRange }[] {
  const monthStart = startOfMonth(today);
  const prevMonthStart = addMonths(monthStart, -1);
  return [
    { label: '7 дней', range: { from: addDays(today, -6), to: today } },
    { label: '30 дней', range: { from: addDays(today, -29), to: today } },
    { label: 'этот месяц', range: { from: monthStart, to: today } },
    {
      label: 'прошлый месяц',
      range: { from: prevMonthStart, to: addDays(monthStart, -1) },
    },
  ];
}

export const DateRangePicker = React.forwardRef<HTMLInputElement, DateRangePickerProps>(
  function DateRangePicker(
    {
      value,
      onChange,
      min,
      max,
      isDisabled,
      presets = true,
      clearable = true,
      className,
      disabled,
      onKeyDown,
      onBlur,
      ...rest
    },
    ref,
  ) {
    const [open, setOpen] = React.useState(false);
    const [text, setText] = React.useState(() => formatRuRange(value));
    const [preview, setPreview] = React.useState('');
    const narrow = useMediaQuery('(max-width: 768px)');
    const today = React.useMemo(() => isoToday(), []);

    React.useEffect(() => {
      setText((current) => {
        const typed = parseRuRange(current);
        return typed.from === value.from && typed.to === value.to
          ? current
          : formatRuRange(value);
      });
    }, [value]);

    const emit = (next: DateRange) => {
      if (next.from === value.from && next.to === value.to) return;
      onChange(next);
    };

    const masked = useMaskedInput({
      value: text,
      mask: maskRangeEdit,
      onChange: (next) => {
        setText(next);
        emit(parseRuRange(next));
      },
    });
    React.useImperativeHandle(ref, () => masked.ref.current as HTMLInputElement, [masked.ref]);

    const pickDay = (iso: string) => {
      const next =
        value.from === '' || value.to !== ''
          ? { from: iso, to: '' }
          : orderRange({ from: value.from, to: iso });
      setPreview('');
      setText(formatRuRange(next));
      emit(next);
      if (next.to !== '') setOpen(false);
    };

    const applyRange = (next: DateRange) => {
      setText(formatRuRange(next));
      emit(next);
      setOpen(false);
    };

    return (
      <PickerShell
        open={open}
        onOpenChange={setOpen}
        disabled={disabled}
        filled={text !== ''}
        finalFocus={masked.ref}
        className={className}
        triggerLabel="Открыть календарь периода"
        onClear={
          clearable && (value.from !== '' || value.to !== '')
            ? () => applyRange({ from: '', to: '' })
            : undefined
        }
        clearLabel="Очистить период"
        popup={
          <Calendar
            mode="range"
            from={value.from}
            to={value.to}
            preview={preview}
            onPreviewChange={setPreview}
            onSelect={pickDay}
            months={narrow ? 1 : 2}
            defaultMonth={value.from || today}
            min={min}
            max={max}
            isDisabled={isDisabled}
            autoFocusDay
            aria-label="Выбор периода"
            footer={
              <>
                {presets &&
                  presetRanges(today).map((preset) => (
                    <Button
                      key={preset.label}
                      size="sm"
                      variant="ghost"
                      onClick={() => applyRange(preset.range)}
                    >
                      {preset.label}
                    </Button>
                  ))}
              </>
            }
          />
        }
      >
        <Input
          ref={masked.ref}
          className={cn(styles.input, styles.rangeInput)}
          value={text}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="off"
          placeholder="дд.мм.гггг — дд.мм.гггг"
          maxLength={23}
          onChange={masked.onChange}
          onKeyDown={(event) => {
            if (event.altKey && event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
            } else if (event.key === 'Escape' && open) {
              event.preventDefault();
              setOpen(false);
            }
            onKeyDown?.(event);
          }}
          onBlur={(event) => {
            const done = completeRange(text);
            setText(done);
            emit(orderRange(parseRuRange(done)));
            onBlur?.(event);
          }}
          {...rest}
        />
      </PickerShell>
    );
  },
);
